import { createSlice } from '@reduxjs/toolkit';
import type { TSystemPreferencesMisc } from '@timeedit/types/lib/types/systemPreferences.type';
import api from '../services/api.service';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from '../utils/sliceHelpers';

export const initialState = {
  loading: false,
  hasErrors: false,
  limitations: {},
  systemPreferences: {},
  updatedSystemPreferences: {},
  variables: [],
};

// Slice
const slice = createSlice({
  name: 'systemPreferences',
  initialState,
  reducers: {
    fetchSystemPreferencesRequest: (state: any) => {
      isLoadingRequest(state);
    },
    fetchSystemPreferencesSuccess: (state: any, { payload }) => {
      state.systemPreferences = payload;
      state.updatedSystemPreferences = payload;
      finishedLoadingSuccess(state);
    },
    fetchSystemPreferenceVariablesSuccess: (state: any, { payload }) => {
      state.variables = payload;
      finishedLoadingSuccess(state);
    },
    fetchSystemPreferencesFailure: (state: any) => {
      finishedLoadingFailure(state);
    },
    fetchLimitationsSystemPreferencesSuccess: (state: any, { payload }) => {
      state.limitations = payload;
      finishedLoadingSuccess(state);
    },
    setsUpdatedSystemPreferences: (state: any, { payload }) => {
      state.updatedSystemPreferences = payload;
    },
    updateSystemPreferencesRequest: (state: any) => {
      isLoadingRequest(state);
    },
    updateSystemPreferencesSuccess: (state: any, { payload }) => {
      state.systemPreferences = payload;
      state.updatedSystemPreferences = payload;
      finishedLoadingSuccess(state);
    },
    updateSystemPreferencesFailure: (state: any) => {
      finishedLoadingFailure(state);
    },
    discardChange: (state: any) => {
      state.updatedSystemPreferences = state.systemPreferences;
    },
  },
});

export default slice.reducer;

// Selectors
export const systemPreferencesLoading = (state: any) =>
  state.systemPreferences.loading;
export const systemPreferencesSelector = (state: any) =>
  state.systemPreferences.systemPreferences as TSystemPreferencesMisc;
export const systemPreferenceVariablesSelector = (state: any) =>
  state.systemPreferences.variables as string[];
export const updatedSystemPreferencesSelector = (state: any) =>
  state.systemPreferences.updatedSystemPreferences;
export const limitationsSystemPreferencesSelector = (state: any) =>
  state.systemPreferences.limitations;

// Actions
export const {
  fetchSystemPreferencesRequest,
  fetchSystemPreferencesSuccess,
  fetchSystemPreferencesFailure,
  fetchLimitationsSystemPreferencesSuccess,
  setsUpdatedSystemPreferences,
  updateSystemPreferencesRequest,
  updateSystemPreferencesSuccess,
  updateSystemPreferencesFailure,
  discardChange,
  fetchSystemPreferenceVariablesSuccess,
} = slice.actions;

export const fetchSystemPreferences = () => async (dispatch: any) => {
  try {
    dispatch(fetchSystemPreferencesRequest());
    const systemPreferences = await api.get({
      endpoint: `/system-preferences`,
    });
    dispatch(fetchSystemPreferencesSuccess(systemPreferences));
  } catch (e) {
    dispatch(fetchSystemPreferencesFailure());
    return console.error(e);
  }
};

export const fetchSystemPreferenceVariables = () => async (dispatch: any) => {
  try {
    dispatch(fetchSystemPreferencesRequest());
    const variables = await api.get({
      endpoint: `/system-preferences/variables`,
    });
    dispatch(fetchSystemPreferenceVariablesSuccess(variables));
  } catch (e) {
    dispatch(fetchSystemPreferencesFailure());
    return console.error(e);
  }
};

export const fetchLimitationsForSystemPreferences =
  () => async (dispatch: any) => {
    try {
      dispatch(fetchSystemPreferencesRequest());
      const limitationsSystemPreferences = await api.get({
        endpoint: `/system-preferences/limitations`,
      });
      dispatch(
        fetchLimitationsSystemPreferencesSuccess(limitationsSystemPreferences)
      );
    } catch (e) {
      dispatch(fetchSystemPreferencesFailure());
      return console.error(e);
    }
  };

export const setUpdatedSystemPreferences =
  (systemPreferences: TSystemPreferencesMisc) => async (dispatch: any) => {
    dispatch(setsUpdatedSystemPreferences(systemPreferences));
  };

export const updateSystemPreferences =
  (systemPreferences: TSystemPreferencesMisc) => async (dispatch: any) => {
    try {
      dispatch(updateSystemPreferencesRequest());
      const { ...SystemPreferencesBody } = systemPreferences;
      const response = await api.patch({
        endpoint: `/system-preferences`,
        data: { ...SystemPreferencesBody },
        suppressNotification: true,
      });
      dispatch(updateSystemPreferencesSuccess(response));
    } catch (e: any) {
      dispatch(updateSystemPreferencesFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  };
